/**
 * TSI Investigation Worksheet, CR-2026-CA-TSI-F1151 (2026-10-13).
 * The TSI Process (A3-TSI-STD-2026-001): RCCA by Tools, Skills, Information, field for field with
 * [Company]-F1.15.1 Rev B. Server: src/routes/tsiInvestigations.js (rules enforced there and in the database).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert, Box, Button, Checkbox, Chip, Container, Divider, FormControlLabel, IconButton, MenuItem, Paper, Radio, RadioGroup,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import ApiCall from "../../utils/apicall";

export const F1151_DOCX = "/templates/coreassure/F1.15.1_TSI_Investigation_Worksheet_RevB.docx";
export const F1151_PDF = "/templates/coreassure/F1.15.1_TSI_Investigation_Worksheet_RevB.pdf";
const COLORS = { T: "#B5701A", S: "#2E9E5B", I: "#1E8AA8" };
const LEGS = [
  { leg: "T", name: "TOOLS", verdict: "tools_verdict", ok: "CONFIRMED PRESENT AND CORRECT",
    help: "List every gage, instrument and piece of equipment the task required. Verify calibration by instrument ID against the calibration log.",
    cols: [["item", "Instrument / equipment"], ["ref", "ID / serial"], ["due_date", "Cal due date"], ["ok", "In cal?"]] },
  { leg: "S", name: "SKILLS", verdict: "skills_verdict", ok: "CONFIRMED CURRENT AND DOCUMENTED",
    help: "Pull the qualification record for the individual who performed the work, not for the department.",
    cols: [["item", "Name / stamp"], ["ref", "Qualification"], ["detail", "Cert No."], ["due_date", "Expires"]] },
  { leg: "I", name: "INFORMATION", verdict: "info_verdict", ok: "CURRENT REVISION IN HAND",
    help: "Identify the exact document revision physically present at the work station when the work was done. Compare it to the current controlled revision.",
    cols: [["item", "Document (drawing, spec, WPS, procedure)"], ["ref", "Rev at station"], ["detail", "Current rev"], ["ok", "Match?"]] },
];
const GAP_OPTIONS = [
  ["T", "T  Tools"], ["S", "S  Skills"], ["I", "I  Information"],
  ["NO_GAP", "NO GAP  All three confirmed with records. Escalate."], ["PENDING", "PENDING  Check not complete. No disposition."],
];
const DISPOSITION = { pending: "Pending", "use-as-is": "Use As Is", rework: "Rework", repair: "Repair", scrap: "Scrap", "return-to-vendor": "Return to Vendor" };
const FIELDS = ["investigation_date", "location_area", "piece_marks", "investigator_name", "nonconformance", "work_performed_on",
  "work_performed_time", "contained_by", "containment_tag", "tools_verdict", "skills_verdict", "info_verdict", "gap_type", "root_cause",
  "qms_element", "car_number", "disposition_detail", "eco_number", "reinspection_result", "reinspection_date", "reinspection_note",
  "client_rep_required", "client_rep_name", "client_rep_signed_on"];

const msgOf = (e, f) => e?.response?.data?.message || e?.response?.data?.error || e?.message || f;

/** The PATCH body: the worksheet fields plus every evidence row. Exported for tests. */
export function worksheetBody(w) {
  const b = {};
  for (const k of FIELDS) b[k] = w[k] ?? null;
  b.evidence = (w.evidence || []).filter((r) => String(r.item || "").trim()).map((r) => ({
    leg: r.leg, item: r.item, ref: r.ref || null, detail: r.detail || null, due_date: r.due_date || null,
    ok: r.ok === true || r.ok === false ? r.ok : null,
  }));
  return b;
}

export default function TsiWorksheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const myId = loginUser?.decodedToken?.id;
  const canEdit = hasPermission(perms, PERMISSIONS.EDIT_NCR);
  const canQcm = hasPermission(perms, PERMISSIONS.CLOSE_NCR);
  const [data, setData] = useState(null);
  const [w, setW] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [eff, setEff] = useState({ result: "EFFECTIVE", note: "" });

  const load = useCallback(async () => {
    try {
      const r = await ApiCall.get(`/ncr/${id}/tsi`);
      setData(r.data.data); setW(r.data.data.worksheet); setDirty(false);
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Could not load the TSI worksheet.") }); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      const d = r?.data;
      if (d?.data) { setW(d.data); setDirty(false); }
      setMsg({ severity: d?.warning ? "warning" : "success", text: d?.warning || ok });
      if (!d?.data) await load();
    } catch (e) {
      const problems = e?.response?.data?.problems;
      setMsg({ severity: "error", text: msgOf(e, "Not saved."), list: problems && problems.length > 1 ? problems : null });
    } finally { setBusy(false); }
  };

  const set = (k, v) => { setW((x) => ({ ...x, [k]: v })); setDirty(true); };
  const setRow = (i, k, v) => { setW((x) => ({ ...x, evidence: x.evidence.map((r, j) => (j === i ? { ...r, [k]: v } : r)) })); setDirty(true); };
  const addRow = (leg) => { setW((x) => ({ ...x, evidence: [...(x.evidence || []), { leg, item: "", ref: "", detail: "", due_date: "", ok: null }] })); setDirty(true); };
  const removeRow = (i) => { setW((x) => ({ ...x, evidence: x.evidence.filter((_, j) => j !== i) })); setDirty(true); };

  if (!data) return <Container sx={{ pt: 3 }}>{msg ? <Alert severity={msg.severity}>{msg.text}</Alert> : "Loading…"}</Container>;
  const ncr = data.ncr;
  const locked = !!w?.locked || !canEdit;
  const tf = (k, label, o = {}) => (
    <TextField size="small" fullWidth={o.full !== false} label={label} disabled={locked} type={o.type || "text"} multiline={!!o.rows} minRows={o.rows}
      InputLabelProps={o.type === "date" ? { shrink: true } : undefined} value={w?.[k] || ""} onChange={(e) => set(k, e.target.value)}
      required={!!o.req} sx={o.sx} />
  );

  return (
    <>
      <Helmet><title>TSI Worksheet {ncr.ncr_number} | SFM</title></Helmet>
      <Container maxWidth="lg">
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
          <Button onClick={() => navigate("/qa-ncr")}>← QA / NCR</Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>TSI Investigation Worksheet</Typography>
          <Chip label={`${ncr.ncr_number} · ${ncr.status}`} />
          {w?.locked && <Chip color="success" label="Signed by QC Manager" />}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            The TSI Process · <span style={{ color: COLORS.T, fontWeight: 700 }}>Tools.</span>{" "}
            <span style={{ color: COLORS.S, fontWeight: 700 }}>Skills.</span>{" "}
            <span style={{ color: COLORS.I, fontWeight: 700 }}>Information.</span> · F1.15.1 Rev B · answer for the moment the work was performed, not for today.
          </Typography>
          <Button size="small" variant="outlined" href={F1151_DOCX} download>Blank form (Word)</Button>
          <Button size="small" href={F1151_PDF} target="_blank" rel="noopener">PDF</Button>
        </Stack>
        {msg && (
          <Alert severity={msg.severity} sx={{ mb: 2 }}>
            {msg.text}
            {msg.list && <ul style={{ margin: "4px 0 0 0" }}>{msg.list.slice(1).map((p) => <li key={p}>{p}</li>)}</ul>}
          </Alert>
        )}

        {!w ? (
          <Paper sx={{ p: 3 }}>
            <Typography sx={{ mb: 2 }}>No TSI worksheet yet. No disposition is authorized until The TSI Process assigns a gap type.</Typography>
            {canEdit && <Button variant="contained" disabled={busy} onClick={() => run(() => ApiCall.post(`/ncr/${id}/tsi`), "Worksheet started.")}>Start the TSI worksheet</Button>}
          </Paper>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
                {tf("investigation_date", "Date", { type: "date" })}
                {tf("location_area", "Location / area")}
                {tf("piece_marks", "Piece mark(s)", { req: true })}
                {tf("investigator_name", "Investigator", { req: true })}
              </Stack>
              <Typography variant="h6" sx={{ mt: 1 }}>1 · The nonconformance</Typography>
              {tf("nonconformance", "What was found, and against what requirement (cite the spec, code or drawing)", { rows: 3, req: true })}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
                {tf("work_performed_on", "Date the work was performed", { type: "date", req: true })}
                {tf("work_performed_time", "Time / shift")}
                {tf("contained_by", "Contained / tagged by")}
                {tf("containment_tag", "Accept-Reject Repair Tag No.")}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">2 · The TSI Process</Typography>
              {LEGS.map((L) => {
                const rows = (w.evidence || []).map((r, i) => ({ r, i })).filter((x) => x.r.leg === L.leg);
                return (
                  <Box key={L.leg} sx={{ mt: 2, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                    <Typography sx={{ fontWeight: 800, color: COLORS[L.leg] }}>{L.leg} · {L.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{L.help}</Typography>
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead><TableRow>{L.cols.map(([k, h]) => <TableCell key={k}>{h}</TableCell>)}<TableCell /></TableRow></TableHead>
                      <TableBody>
                        {rows.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="caption">No records listed.</Typography></TableCell></TableRow>}
                        {rows.map(({ r, i }) => (
                          <TableRow key={i}>
                            {L.cols.map(([k]) => (
                              <TableCell key={k}>
                                {k === "ok" ? (
                                  <TextField select size="small" disabled={locked} value={r.ok === true ? "Y" : r.ok === false ? "N" : ""}
                                    onChange={(e) => setRow(i, "ok", e.target.value === "Y" ? true : e.target.value === "N" ? false : null)} sx={{ minWidth: 70 }}>
                                    <MenuItem value="">Select</MenuItem><MenuItem value="Y">Y</MenuItem><MenuItem value="N">N</MenuItem>
                                  </TextField>
                                ) : (
                                  <TextField size="small" fullWidth disabled={locked} type={k === "due_date" ? "date" : "text"}
                                    InputLabelProps={k === "due_date" ? { shrink: true } : undefined} value={r[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} />
                                )}
                              </TableCell>
                            ))}
                            <TableCell>{!locked && <IconButton size="small" onClick={() => removeRow(i)} aria-label="Remove row">✕</IconButton>}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {!locked && <Button size="small" onClick={() => addRow(L.leg)}>Add {L.leg === "T" ? "instrument" : L.leg === "S" ? "person" : "document"}</Button>}
                    <RadioGroup row value={w[L.verdict] || ""} onChange={(e) => set(L.verdict, e.target.value)}>
                      <FormControlLabel disabled={locked} value="CONFIRMED" control={<Radio />} label={<span style={{ color: COLORS.S, fontWeight: 700 }}>{L.ok}</span>} />
                      <FormControlLabel disabled={locked} value="GAP" control={<Radio />} label={<span style={{ color: "#B02020", fontWeight: 700 }}>GAP FOUND</span>} />
                    </RadioGroup>
                  </Box>
                );
              })}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">3 · Gap type</Typography>
              <Typography variant="caption" color="text.secondary">Select exactly one. One NCR, one gap type. Do not split across buckets.</Typography>
              <RadioGroup value={w.gap_type || "PENDING"} onChange={(e) => set("gap_type", e.target.value)}>
                {GAP_OPTIONS.map(([v, l]) => (
                  <FormControlLabel key={v} disabled={locked} value={v} control={<Radio />}
                    label={<span style={{ fontWeight: 700, color: COLORS[v] || (v === "PENDING" ? "#B7791F" : "#2E9E5B") }}>{l}</span>} />
                ))}
              </RadioGroup>
              <Alert severity="info" sx={{ mt: 1 }}>
                NO GAP is a legitimate answer. If all three legs are confirmed with records, the cause is elsewhere: material defect, design or detailing error,
                equipment failure in service, or deliberate deviation. Escalate it. Do not manufacture a gap. PENDING is not a resting place.
              </Alert>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">4 · Root cause statement</Typography>
              <Typography variant="caption" color="text.secondary">
                Name the artifact, not the behavior. Acceptable: "Gage 04 calibration lapsed 3/12, in use 3/19." Not acceptable: "Human error."
              </Typography>
              <Box sx={{ mt: 1 }}>{tf("root_cause", "Root cause (the specific gage ID, cert, WPS or drawing revision)", { rows: 3, req: true })}</Box>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
                {tf("qms_element", "Governing QMS element", { req: true })}
                {tf("car_number", "Corrective Action Data Log No. (CAR)", { req: true })}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">5 · Disposition and closure</Typography>
              <Typography variant="body2" sx={{ my: 1 }}>
                Disposition on the NCR: <strong>{DISPOSITION[ncr.disposition] || ncr.disposition}</strong>
                {ncr.disposition === "pending" && " · set it on the NCR once the gap type is assigned"}
              </Typography>
              {tf("disposition_detail", "Disposition detail (what, to which revision, assigned date)", { rows: 2 })}
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>{tf("eco_number", "Engineering Change Order No. (if applicable)")}</Stack>
              {["rework", "repair"].includes(ncr.disposition) && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>QCI re-inspection of rework / repair</Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
                    <TextField select size="small" label="Result" disabled={locked} value={w.reinspection_result || ""}
                      onChange={(e) => set("reinspection_result", e.target.value || null)} sx={{ minWidth: 180 }}>
                      <MenuItem value="">Select</MenuItem><MenuItem value="ACCEPTED">Accepted</MenuItem><MenuItem value="REJECTED">Rejected</MenuItem>
                    </TextField>
                    {tf("reinspection_date", "Date", { type: "date", full: false })}
                  </Stack>
                  <Box sx={{ mt: 1 }}>{tf("reinspection_note", "Re-inspection result", { rows: 2 })}</Box>
                </Box>
              )}
              <FormControlLabel sx={{ mt: 1 }} disabled={locked}
                control={<Checkbox checked={!!w.client_rep_required} onChange={(e) => set("client_rep_required", e.target.checked)} />}
                label="Client / owner representative sign-off required by contract" />
              {w.client_rep_required && (
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                  {tf("client_rep_name", "Client / owner rep (name)")}
                  {tf("client_rep_signed_on", "Signed on", { type: "date" })}
                </Stack>
              )}
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6">Sign-off</Typography>
              <Typography variant="body2">
                Investigator: {w.investigator_signed_at ? `signed ${String(w.investigator_signed_at).slice(0, 16).replace("T", " ")} (user #${w.investigator_signed_by})` : "not signed"}
              </Typography>
              <Typography variant="body2">
                QC Manager: {w.qc_manager_signed_at ? `signed ${String(w.qc_manager_signed_at).slice(0, 16).replace("T", " ")} (user #${w.qc_manager_signed_by})` : "not signed"}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }} useFlexGap>
                {!w.locked && canEdit && (
                  <Button variant="contained" disabled={busy || !dirty}
                    onClick={() => run(() => ApiCall.patch(`/ncr/${id}/tsi`, worksheetBody(w)), "Saved.")}>Save</Button>
                )}
                {!w.locked && canEdit && !w.investigator_signed_at && (
                  <Button variant="outlined" disabled={busy || dirty}
                    onClick={() => run(() => ApiCall.post(`/ncr/${id}/tsi/sign`, { role: "INVESTIGATOR" }), "Signed as investigator.")}>Sign as investigator</Button>
                )}
                {!w.locked && canQcm && w.investigator_signed_at && Number(w.investigator_signed_by) !== Number(myId) && (
                  <Button variant="contained" color="success" disabled={busy || dirty}
                    onClick={() => run(() => ApiCall.post(`/ncr/${id}/tsi/sign`, { role: "QC_MANAGER" }), "Signed as QC Manager. The worksheet is locked.")}>Sign as QC Manager</Button>
                )}
                {!w.locked && w.investigator_signed_at && Number(w.investigator_signed_by) === Number(myId) && (
                  <Typography variant="caption" sx={{ alignSelf: "center" }}>A different person signs as QC Manager.</Typography>
                )}
              </Stack>
            </Paper>

            {w.locked && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6">Effectiveness verification</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>Re-pull the same record at 30 / 60 days. Due: <strong>{w.effectiveness_due || "Not set"}</strong></Typography>
                {w.effectiveness_verified_on ? (
                  <Alert severity={w.effectiveness_result === "EFFECTIVE" ? "success" : "error"}>
                    {w.effectiveness_result === "EFFECTIVE" ? "Effective" : "Not effective"} · verified {w.effectiveness_verified_on} by user #{w.effectiveness_verified_by} · {w.effectiveness_note}
                  </Alert>
                ) : canQcm ? (
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
                    <TextField select size="small" label="Result" value={eff.result} onChange={(e) => setEff({ ...eff, result: e.target.value })} sx={{ minWidth: 180 }}>
                      <MenuItem value="EFFECTIVE">Effective</MenuItem><MenuItem value="NOT_EFFECTIVE">Not effective</MenuItem>
                    </TextField>
                    <TextField size="small" fullWidth label="Which record was re-pulled, and what it showed" value={eff.note} onChange={(e) => setEff({ ...eff, note: e.target.value })} />
                    <Button variant="contained" disabled={busy || !eff.note.trim()}
                      onClick={() => run(() => ApiCall.post(`/ncr/${id}/tsi/effectiveness`, eff), "Effectiveness recorded.")}>Record</Button>
                  </Stack>
                ) : <Typography variant="caption">Not yet verified.</Typography>}
              </Paper>
            )}
            <Divider sx={{ mb: 3 }} />
          </>
        )}
      </Container>
    </>
  );
}
